// tag-highlighter.js - Blue highlight for #tag syntax (Obsidian-style)

import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin } from "@codemirror/view";

// Decoration for tag content (cursor outside - # hidden, full border-radius)
const tagContentHidden = Decoration.mark({
    class: "cm-tag cm-tag-marker-outside"
});

// Decoration for tag content (cursor inside - # visible, right border-radius only)
const tagContentVisible = Decoration.mark({
    class: "cm-tag"
});

// Decoration for the # marker (hidden)
const tagMarkerHidden = Decoration.mark({
    class: "cm-tag-marker-hidden"
});

// Decoration for the # marker (visible)
const tagMarkerVisible = Decoration.mark({
    class: "cm-tag-marker"
});

function findTags(view) {
    const doc = view.state.doc;
    const text = doc.toString();
    const cursorPos = view.state.selection.main.head;

    const decorations = [];

    // Match tags: # followed by letters, numbers, underscores, hyphens
    const regex = /(?<![a-zA-Z0-9_])#([a-zA-Z0-9_\u00C0-\u017F]+(?:[-_\/][a-zA-Z0-9_\u00C0-\u017F]+)*)/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
        const fullStart = match.index;
        const fullEnd = match.index + match[0].length;
        const contentStart = fullStart + 1; // After the #

        // Check if cursor is inside this tag
        const cursorInside = cursorPos >= fullStart && cursorPos <= fullEnd;

        if (cursorInside) {
            // Cursor inside: show # marker, content has partial border-radius
            decorations.push({ from: fullStart, to: contentStart, deco: tagMarkerVisible });
            decorations.push({ from: contentStart, to: fullEnd, deco: tagContentVisible });
        } else {
            // Cursor outside: hide # marker, content has full border-radius
            decorations.push({ from: fullStart, to: contentStart, deco: tagMarkerHidden });
            decorations.push({ from: contentStart, to: fullEnd, deco: tagContentHidden });
        }
    }

    // Sort by position and build RangeSet
    decorations.sort((a, b) => a.from - b.from);

    const builder = new RangeSetBuilder();
    for (const { from, to, deco } of decorations) {
        builder.add(from, to, deco);
    }

    return builder.finish();
}

export const tagHighlighter = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = findTags(view);
    }

    update(update) {
        if (update.docChanged || update.viewportChanged || update.selectionSet) {
            this.decorations = findTags(update.view);
        }
    }
}, {
    decorations: v => v.decorations
});
